import * as React from 'react';

import { cn } from '@/lib/utils';

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="relative w-full overflow-auto">
      <table ref={ref} className={cn('w-full caption-bottom text-sm', className)} {...props} />
    </div>
  )
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      // A definite band, and a rule heavier than the hairlines between rows so
      // the first row of data does not read as part of the header.
      'bg-table-header [&_tr]:border-b [&_tr]:border-table-header-border',
      className
    )}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        // Hover and selection stay lighter than the header band. They used to
        // use the same `muted` token, so hovering the first row made it
        // indistinguishable from the header above it.
        // The lighter weight. A row divider is rhythm inside an object, not the
      // edge of one, and at `--border` sixteen of them ruled the table into a
      // stack of boxes and out-shouted the card outline containing them.
      'border-b border-subtle transition-colors hover:bg-row-hover data-[state=selected]:bg-accent/50',
        className
      )}
      {...props}
    />
  )
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'h-12 px-4 text-left align-middle',
      'text-table-header',
      '[&:has([role=checkbox])]:pr-0',
      className
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  /** Render in JetBrains Mono — for SKUs, references and other identifiers. */
  mono?: boolean;
}

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, mono, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        'px-4 py-2 align-middle leading-tight',
        'text-table-cell',
        mono && 'font-mono',
        '[&:has([role=checkbox])]:pr-0',
        className
      )}
      {...props}
    />
  )
);
TableCell.displayName = 'TableCell';

/**
 * Two-line table cell: a bold primary identifier over a muted secondary
 * reference. `mono` applies to the primary line only, where identifiers live.
 */
const TableCellStack = React.forwardRef<
  HTMLTableCellElement,
  TableCellProps & { primary: React.ReactNode; secondary?: React.ReactNode }
>(({ className, primary, secondary, mono, ...props }, ref) => (
  <td ref={ref} className={cn('px-4 py-2 align-middle leading-tight', className)} {...props}>
    <div className={cn('text-sm font-semibold text-foreground', mono && 'font-mono')}>
      {primary}
    </div>
    {secondary != null && secondary !== '' && (
      <div className="mt-0.5 text-xs text-muted-foreground">{secondary}</div>
    )}
  </td>
));
TableCellStack.displayName = 'TableCellStack';

/**
 * Consistent placeholder for missing values — an em dash, never a blank cell.
 */
const EmptyValue = ({ className }: { className?: string }) => (
  <span className={cn('text-muted-foreground', className)} aria-label="No value">
    —
  </span>
);

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
));
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCellStack,
  TableCaption,
  EmptyValue,
};
